import React, { useState } from "react";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import {
  Wallet,
  ArrowRight,
  DollarSign,
  Fuel,
  Car,
  Key,
  Wrench,
  Landmark,
  Gift,
  Shield,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// XGO FINANCIAL SYSTEM — GUIDE SCREEN
//
// Purpose: this is the one place that explains, in plain language, every place real money moves
// in the XGO platform — which subsystem does what, what the folio/trxn_code short codes on a
// ledger row actually mean, and where to go in this dashboard to act on each one. It intentionally
// mirrors the structure of xgo_rideshare_backend/Documentation/FINANCIAL_SYSTEM.md (the backend-side
// version of this same map) so a developer, an AI assistant, and a dashboard user are always looking
// at the same mental model, described three times at three different levels of detail — never three
// different stories.
//
// This screen is deliberately static/reference content, not a live data screen — it doesn't fetch
// anything. If a subsystem's real behavior changes, update this file and the backend doc together.

const SUBSYSTEMS = [
  {
    key: "ledger",
    name: "Core Ledger",
    icon: Wallet,
    color: "blue",
    table: "financial_top_up",
    summary:
      "The original ledger. Every commission, withdrawal, and promotion transfer is recorded here as a row with a folio (which wallet) and a trxn_code (why the money moved).",
    detail:
      "Five wallets live in this one table, distinguished by the folio column: UW (a person's own balance), RW (XGO's revenue), VW (a marketplace vendor's balance), MW (a running total across UW+VW+RW — XGO's overall cash position), and EW (money held in escrow, e.g. security deposits).",
    link: { label: "Revenue Breakdown Report", path: "/revenue-breakdown-report" },
  },
  {
    key: "allocation",
    name: "Earnings Allocation",
    icon: DollarSign,
    color: "green",
    table: "financial_driver_earnings, financial_earnings_allocation_log",
    summary:
      "Every time a trip ends, this splits the fare (after commission) between the driver and — depending on that driver's vehicle-ownership tier — the fuel program, vehicle rental, and vehicle maintenance accounts.",
    detail:
      "Runs automatically, no screen to click. What actually gets deducted depends entirely on the driver's tier — see Vehicle Ownership Tiers below.",
    link: { label: "Revenue Breakdown Report", path: "/revenue-breakdown-report" },
  },
  {
    key: "fuel",
    name: "Fuel Program",
    icon: Fuel,
    color: "amber",
    table: "financial_fuel_program",
    summary:
      "A per-trip fuel rebate for drivers on the two XGO-financed tiers, paid out as $20 coupons. The rate is priced per liter, not a flat per-km number.",
    detail:
      "Rate = fuel price per liter ÷ km-per-liter, both set in Vehicle Ownership & Rates — change either number there and it applies to the very next trip, no deploy needed.",
    link: { label: "Vehicle Ownership & Rates", path: "/vehicle-ownership-settings" },
  },
  {
    key: "ownership",
    name: "Vehicle Ownership Tiers",
    icon: Car,
    color: "purple",
    table: "financial_vehicle_rentals, financial_vehicle_owners",
    summary:
      "The switch that decides which deductions apply to a given driver. Every driver defaults to Independent (commission only) until an admin sets something else.",
    detail:
      "Independent — driver keeps everything after commission. XGO Owned & Managed — full split (fuel, rental, maintenance all to XGO). Owner-Financed, XGO-Managed — maintenance to XGO, but the rental share goes to the vehicle's real owner, not XGO. Drive to Own — fuel + maintenance, plus a fixed monthly loan repayment instead of a percentage rental.",
    link: { label: "Vehicle Ownership & Rates", path: "/vehicle-ownership-settings" },
  },
  {
    key: "drivetoown",
    name: "Drive to Own",
    icon: Key,
    color: "indigo",
    table: "financial_drive_to_own_plans, financial_drive_to_own_applications",
    summary:
      "XGO's rent-to-buy program. A driver applies to a specific vehicle+repayment plan; once approved and the vehicle is collected, their tier flips to Drive to Own and repayment starts automatically.",
    detail:
      "Review and approve applications here. A month that ends short of its full repayment target isn't treated as a missed payment — it just extends the loan a little; nothing goes to collections automatically.",
    link: { label: "Drive to Own Applications", path: "/drive-to-own-applications" },
  },
  {
    key: "teller",
    name: "Teller / Office Cash",
    icon: Landmark,
    color: "slate",
    table: "financial_tellers, financial_teller_transactions, financial_daily_cash_balance",
    summary:
      "Physical cash handled by office staff — rental deposits, wallet top-ups taken in person, and cash disbursements. Kept deliberately separate from the digital wallet ledger.",
    detail:
      "Every cash movement here is meant to leave five linked records: the teller transaction, the resulting wallet credit, the daily cash balance, an audit log entry, and day-end reconciliation — the most complete audit trail of any subsystem in the platform.",
    link: null,
  },
  {
    key: "promotions",
    name: "Promotions",
    icon: Gift,
    color: "pink",
    table: "promotion_wallet / financial_promo_wallet",
    summary:
      "Signup bonuses, referral rewards, and marketing budgets — funded either from XGO's Revenue Wallet or from fresh external cash.",
    detail:
      "A promotion balance is not the same as spendable wallet money or driver withdrawable earnings, even when it lands in the same person's account — it's tracked and reported separately.",
    link: { label: "Enhanced Promotions", path: "/enhanced-promotions" },
  },
  {
    key: "escrow",
    name: "Escrow",
    icon: Shield,
    color: "gray",
    table: "financial_top_up (folio EW)",
    summary:
      "Money held temporarily for security deposits or a dispute still being resolved — not spendable by anyone until it's released.",
    detail:
      "Lives in the same core ledger table as the other wallets, under its own folio.",
    link: null,
  },
];

const FOLIOS = [
  { code: "UW", name: "User Wallet", meaning: "A customer's or driver's own balance." },
  { code: "RW", name: "Revenue Wallet", meaning: "XGO's own commission/revenue holding." },
  { code: "VW", name: "Vendor Wallet", meaning: "A marketplace vendor's balance." },
  { code: "MW", name: "Main Wallet", meaning: "A running total across UW + VW + RW — XGO's overall cash position at a glance." },
  { code: "EW", name: "Escrow Wallet", meaning: "Money held for a security deposit or dispute, not yet released to anyone." },
];

const TRXN_CODES = [
  { code: "CM", meaning: "Commission", note: "Taken on every trip, private or shared." },
  { code: "WTD", meaning: "Withdrawal", note: "Money leaving a wallet to the outside world." },
  { code: "TU", meaning: "Top Up", note: "Money entering a wallet from the outside world." },
  { code: "PM", meaning: "Promotion", note: "A promotional reward credited to a wallet." },
  { code: "MP", meaning: "Mischief Penalty", note: "A penalty charge against a user or vendor." },
  { code: "REFBN", meaning: "Referral Bonus", note: "A reward for a successful referral." },
  { code: "DISCR", meaning: "Discount", note: "Compensates a driver for a passenger's promo discount on a trip." },
  { code: "COMOFF", meaning: "Commission Offset", note: "Uses a driver's existing promo balance to reduce a commission charge." },
];

const COLOR_CLASSES = {
  blue: { bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-600 dark:text-blue-400", ring: "border-blue-200 dark:border-blue-800" },
  green: { bg: "bg-green-50 dark:bg-green-900/20", text: "text-green-600 dark:text-green-400", ring: "border-green-200 dark:border-green-800" },
  amber: { bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-600 dark:text-amber-400", ring: "border-amber-200 dark:border-amber-800" },
  purple: { bg: "bg-purple-50 dark:bg-purple-900/20", text: "text-purple-600 dark:text-purple-400", ring: "border-purple-200 dark:border-purple-800" },
  indigo: { bg: "bg-indigo-50 dark:bg-indigo-900/20", text: "text-indigo-600 dark:text-indigo-400", ring: "border-indigo-200 dark:border-indigo-800" },
  slate: { bg: "bg-slate-50 dark:bg-slate-900/20", text: "text-slate-600 dark:text-slate-400", ring: "border-slate-200 dark:border-slate-800" },
  pink: { bg: "bg-pink-50 dark:bg-pink-900/20", text: "text-pink-600 dark:text-pink-400", ring: "border-pink-200 dark:border-pink-800" },
  gray: { bg: "bg-gray-50 dark:bg-gray-900/20", text: "text-gray-600 dark:text-gray-400", ring: "border-gray-200 dark:border-gray-800" },
};

const SubsystemCard = ({ subsystem }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const Icon = subsystem.icon;
  const colors = COLOR_CLASSES[subsystem.color];

  return (
    <div className={`border rounded-lg p-4 bg-white dark:bg-gray-800 ${colors.ring}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors.bg}`}>
            <Icon className={colors.text} size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{subsystem.name}</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">{subsystem.table}</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">{subsystem.summary}</p>
      {open && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 border-t border-gray-100 dark:border-gray-700 pt-2">
          {subsystem.detail}
        </p>
      )}
      {subsystem.link && (
        <button
          onClick={() => navigate(subsystem.link.path)}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          Go to {subsystem.link.label} <ExternalLink size={14} />
        </button>
      )}
    </div>
  );
};

const FinancialSystemGuide = () => {
  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="text-blue-500" size={28} />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">XGO Financial System</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
            A plain-language map of every place real money moves in XGO — what each part does, what the
            short codes on a ledger entry mean, and where to go in this dashboard to act on each one.
          </p>
        </div>

        {/* System map */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">The subsystems</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SUBSYSTEMS.map((s) => (
              <SubsystemCard key={s.key} subsystem={s} />
            ))}
          </div>
        </div>

        {/* Money flow */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">How money moves through one trip</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 overflow-x-auto">
            <div className="flex items-center gap-2 flex-wrap text-sm font-medium">
              <span className="px-3 py-1.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">Passenger pays fare</span>
              <ArrowRight className="text-gray-400" size={16} />
              <span className="px-3 py-1.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Commission taken (RW)</span>
              <ArrowRight className="text-gray-400" size={16} />
              <span className="px-3 py-1.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">Split by ownership tier</span>
              <ArrowRight className="text-gray-400" size={16} />
              <span className="px-3 py-1.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">Fuel / Rental / Maintenance</span>
              <ArrowRight className="text-gray-400" size={16} />
              <span className="px-3 py-1.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">Driver earnings</span>
              <ArrowRight className="text-gray-400" size={16} />
              <span className="px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Withdrawal</span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
              An Independent-tier driver (the default for every driver until set otherwise) skips the Fuel/Rental/Maintenance step entirely — they keep everything after commission.
            </p>
          </div>
        </div>

        {/* Glossaries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Folio — which wallet</h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Code</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Wallet</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Meaning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {FOLIOS.map((f) => (
                    <tr key={f.code}>
                      <td className="px-4 py-2 font-mono font-semibold text-gray-900 dark:text-white">{f.code}</td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{f.name}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{f.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Transaction code — why the money moved</h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Code</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Meaning</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-300">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {TRXN_CODES.map((t) => (
                    <tr key={t.code}>
                      <td className="px-4 py-2 font-mono font-semibold text-gray-900 dark:text-white">{t.code}</td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{t.meaning}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{t.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Folio (which wallet) and transaction code (why it moved) are two different fields on the same ledger row — easy to mix up, so they're kept as two separate tables here on purpose.
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-500 mt-8">
          This page mirrors <code className="font-mono">xgo_rideshare_backend/Documentation/FINANCIAL_SYSTEM.md</code> — if
          the two ever disagree, the backend doc is the source of truth and this page should be updated to match.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default FinancialSystemGuide;
